import { useState, useEffect, useMemo, useCallback } from 'react';

// Shared with the bank field on the Invitations tab — same suggestion list.
const BANK_LIST_ID = 'hq-bank-options';

// Quality Check — every purchased and cancelled order we have logged, with each
// datapoint broken down side by side so the difference between the two can be
// read directly.
//
// Deliberately does not pronounce a verdict. Most dimensions here rest on a
// handful of records, and a 100% rate over two orders means nothing; the job is
// to lay the data out with its base counts visible and let it be judged.

const OUT = {
  purchased: { label: 'Purchased', color: '#34d399', bg: 'rgba(52,211,153,0.18)' },
  cancelled: { label: 'Cancelled', color: '#f87171', bg: 'rgba(248,113,113,0.18)' },
};

// Below this a rate is noise, and is shown greyed with the count leading.
const THIN = 4;

const CARD = {
  background: 'rgba(0,0,0,0.22)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: 12,
};

const CELL = { padding: '7px 12px', whiteSpace: 'nowrap', verticalAlign: 'top' };

// ─── Derived fields ──────────────────────────────────────────────────────────

// Outward half of a UK postcode (B21 9AX -> B21). The unit half varies between
// orders for the same house, the district does not.
function district(postcode) {
  const p = (postcode || '').replace(/\s+/g, '').toUpperCase();
  return p.length > 3 ? p.slice(0, -3) : (p || null);
}

// A digit standing in for a letter inside a word — "Wh1tehall", "N3wcrest".
function hasDigitSwap(line1) {
  return (line1 || '').split(/\s+/).some(t => /[a-z][0-9]|[0-9][a-z]/i.test(t) && !/^\d+[a-z]?$/i.test(t));
}

function hoursBetween(a, b) {
  if (!a || !b) return null;
  return (new Date(b).getTime() - new Date(a).getTime()) / 3_600_000;
}

function bucketHours(h) {
  if (h === null) return null;
  if (h < 1)  return 'under 1h';
  if (h < 6)  return '1–6h';
  if (h < 24) return '6–24h';
  if (h < 48) return '24–48h';
  return '48h+';
}

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function enrich(entry) {
  const inv  = entry.invite || {};
  const addr = entry.address || {};
  const acted = hoursBetween(inv.emailDate, entry.loggedAt);
  const when  = entry.loggedAt ? new Date(entry.loggedAt) : null;
  const reference = !!entry.reference;
  return {
    id: entry.id,
    outcome: entry.outcome,
    reference,
    manual: !!entry.manual,
    note: entry.note || null,
    // A reference pattern carries no real order date, so anything derived from
    // one is unknown rather than whatever moment it happened to be filed.
    loggedAt: reference ? null : (entry.loggedAt || null),
    bank: entry.bank || null,
    line1: addr.line1 || null,
    line2: addr.line2 || null,
    postcode: addr.postcode || null,
    district: district(addr.postcode),
    digitSwap: addr.line1 ? (hasDigitSwap(addr.line1) ? 'yes' : 'no') : null,
    asin: inv.asin || null,
    product: inv.summary || null,
    account: inv.accountEmail || null,
    domain: inv.accountEmail ? (inv.accountEmail.split('@')[1] || null) : null,
    region: inv.region || null,
    inviteType: inv.type || null,
    inviteDate: inv.emailDate || null,
    actedWithin: reference ? null : bucketHours(acted),
    actedHours: reference ? null : acted,
    dayOfWeek: reference || !when ? null : DOW[when.getDay()],
    hourOfDay: reference || !when ? null : `${String(when.getHours()).padStart(2, '0')}:00`,
  };
}

// Every dimension worth comparing, in the order it is shown.
const DIMENSIONS = [
  { key: 'bank',         label: 'Bank used' },
  { key: 'district',     label: 'Postcode district' },
  { key: 'postcode',     label: 'Full postcode' },
  { key: 'line1',        label: 'Address line 1' },
  { key: 'line2',        label: 'Address line 2' },
  { key: 'digitSwap',    label: 'Digit in place of a letter' },
  { key: 'domain',       label: 'Account domain' },
  { key: 'asin',         label: 'ASIN' },
  { key: 'actedWithin',  label: 'Time from invite to order' },
  { key: 'dayOfWeek',    label: 'Day ordered' },
  { key: 'hourOfDay',    label: 'Hour ordered' },
];

// Every column available in the raw table / CSV.
const COLUMNS = [
  ['loggedAt', 'Logged'], ['outcome', 'Outcome'], ['bank', 'Bank'],
  ['line1', 'Line 1'], ['line2', 'Line 2'], ['postcode', 'Postcode'], ['district', 'District'],
  ['product', 'Product'], ['asin', 'ASIN'], ['account', 'Account'], ['domain', 'Domain'],
  ['region', 'Region'], ['inviteType', 'Invite type'], ['inviteDate', 'Invite received'],
  ['actedWithin', 'Acted within'], ['dayOfWeek', 'Day'], ['hourOfDay', 'Hour'],
];

function breakdown(rows, key) {
  const map = new Map();
  let missing = 0;
  for (const r of rows) {
    const v = r[key];
    if (v === null || v === undefined || v === '') { missing++; continue; }
    if (!map.has(v)) map.set(v, { value: v, purchased: 0, cancelled: 0 });
    map.get(v)[r.outcome]++;
  }
  const values = [...map.values()].map(v => {
    const total = v.purchased + v.cancelled;
    return { ...v, total, rate: total ? v.purchased / total : null };
  }).sort((a, b) => b.total - a.total || (b.rate ?? 0) - (a.rate ?? 0));
  return { values, missing, covered: rows.length - missing };
}

function rateColor(rate, total) {
  if (rate === null) return '#475569';
  if (total < THIN)  return '#64748b';
  if (rate >= 0.8)   return '#34d399';
  if (rate >= 0.5)   return '#fbbf24';
  return '#f87171';
}

// ─── Pieces ──────────────────────────────────────────────────────────────────

function Tile({ label, value, sub, accent }) {
  return (
    <div style={{ ...CARD, flex: 1, minWidth: 116, padding: '12px 15px' }}>
      <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: 21, fontWeight: 700, color: accent || '#f1f5f9', marginTop: 3, letterSpacing: '-0.4px' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#475569', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

// Purchased vs cancelled as one bar, so the split is legible without reading numbers.
function SplitBar({ purchased, cancelled }) {
  const total = purchased + cancelled || 1;
  return (
    <div style={{ display: 'flex', height: 5, borderRadius: 3, overflow: 'hidden', background: 'rgba(255,255,255,0.06)', minWidth: 60 }}>
      <div style={{ width: `${(purchased / total) * 100}%`, background: OUT.purchased.color }} />
      <div style={{ width: `${(cancelled / total) * 100}%`, background: OUT.cancelled.color }} />
    </div>
  );
}

function DimensionCard({ label, rows, dimKey, isMobile }) {
  const { values, missing, covered } = breakdown(rows, dimKey);
  const [open, setOpen] = useState(false);
  if (!values.length) return null;

  const shown = open ? values : values.slice(0, 6);
  // A dimension only separates the two outcomes if it has more than one value
  const splits = values.length > 1;

  return (
    <div style={{ ...CARD, overflow: 'hidden' }}>
      <div style={{ padding: isMobile ? '11px 12px' : '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>{label}</span>
          <span style={{ fontSize: 10, color: '#475569' }}>
            {covered} of {rows.length} recorded{missing ? ` · ${missing} blank` : ''}
          </span>
        </div>
        {!splits && (
          <div style={{ fontSize: 10, color: '#475569', marginTop: 3 }}>
            one value only — cannot separate the two outcomes
          </div>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
              {['Value', 'Purchased', 'Cancelled', 'Split', '% purchased'].map(h => (
                <th key={h} style={{ ...CELL, textAlign: 'left', fontWeight: 500, color: '#64748b' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map(v => (
              <tr key={String(v.value)} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                <td style={{ ...CELL, color: '#e2e8f0', maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={String(v.value)}>
                  {String(v.value)}
                </td>
                <td style={{ ...CELL, color: v.purchased ? OUT.purchased.color : '#334155', fontWeight: 600 }}>{v.purchased}</td>
                <td style={{ ...CELL, color: v.cancelled ? OUT.cancelled.color : '#334155', fontWeight: 600 }}>{v.cancelled}</td>
                <td style={{ ...CELL, width: 90 }}><SplitBar purchased={v.purchased} cancelled={v.cancelled} /></td>
                <td style={{ ...CELL, fontWeight: 700, color: rateColor(v.rate, v.total) }}>
                  {Math.round(v.rate * 100)}%
                  <span style={{ color: '#475569', fontWeight: 400 }}> ({v.total})</span>
                  {v.total < THIN && <span style={{ color: '#475569', fontWeight: 400 }} title={`only ${v.total} order${v.total === 1 ? '' : 's'} — not meaningful`}> ·  thin</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {values.length > 6 && (
        <button onClick={() => setOpen(o => !o)} style={{
          width: '100%', background: 'rgba(255,255,255,0.03)', border: 'none',
          borderTop: '1px solid rgba(255,255,255,0.05)', color: '#64748b',
          fontSize: 11, padding: '7px 0', cursor: 'pointer',
        }}>
          {open ? 'Show fewer' : `Show all ${values.length} values`}
        </button>
      )}
    </div>
  );
}

function RawTable({ rows, isMobile, onEdit }) {
  const [sort, setSort] = useState({ key: 'loggedAt', dir: -1 });

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const av = a[sort.key], bv = b[sort.key];
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    return (av > bv ? 1 : -1) * sort.dir;
  }), [rows, sort]);

  const fmt = (key, v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (key === 'loggedAt' || key === 'inviteDate') {
      return new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
    }
    return String(v);
  };

  return (
    <div style={{ ...CARD, overflow: 'hidden' }}>
      <div style={{ padding: isMobile ? '11px 12px' : '12px 16px', fontSize: 11, color: '#475569', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        Every logged purchased and cancelled order, with each datapoint stored against it.
        Click a heading to sort, or <span style={{ color: '#a5b4fc' }}>Edit</span> to amend a record.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
              <th style={{ ...CELL, textAlign: 'left', fontWeight: 500, color: '#64748b' }} />
              {COLUMNS.map(([key, label]) => (
                <th key={key}
                    onClick={() => setSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }))}
                    style={{ ...CELL, textAlign: 'left', fontWeight: 500, color: sort.key === key ? '#94a3b8' : '#64748b', cursor: 'pointer', userSelect: 'none' }}>
                  {label}{sort.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.id} style={{
                borderTop: '1px solid rgba(255,255,255,0.04)',
                background: r.reference ? 'rgba(129,140,248,0.07)'
                  : r.outcome === 'cancelled' ? 'rgba(248,113,113,0.05)' : 'transparent',
              }}>
                <td style={{ ...CELL, paddingRight: 4 }}>
                  {r.reference && (
                    <span title="Reference pattern — not an order you placed" style={{
                      fontSize: 9, fontWeight: 700, color: '#a5b4fc', background: 'rgba(129,140,248,0.18)',
                      borderRadius: 5, padding: '1px 5px', marginRight: 5,
                    }}>REF</span>
                  )}
                  <button onClick={() => onEdit(r)} title={r.manual ? 'Entered by hand — edit' : 'Edit this record'} style={{
                    fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 6, cursor: 'pointer',
                    border: '1px solid rgba(99,102,241,0.35)', background: 'rgba(99,102,241,0.12)', color: '#a5b4fc',
                  }}>
                    {r.manual ? '✎ ✋' : '✎'}
                  </button>
                </td>
                {COLUMNS.map(([key]) => (
                  <td key={key} style={{
                    ...CELL,
                    color: key === 'outcome' ? OUT[r.outcome].color : '#94a3b8',
                    fontWeight: key === 'outcome' ? 700 : 400,
                    maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis',
                  }} title={fmt(key, r[key])}>
                    {key === 'outcome' ? OUT[r.outcome].label : fmt(key, r[key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Editing ─────────────────────────────────────────────────────────────────

// datetime-local speaks local time with no zone; the store speaks UTC ISO.
function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const INPUT = {
  background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6,
  color: '#e2e8f0', fontSize: 12, padding: '6px 8px', width: '100%',
  boxSizing: 'border-box', colorScheme: 'dark',
};

function Field({ label, children, hint }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 9, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: 9, color: '#334155' }}>{hint}</span>}
    </label>
  );
}

const BLANK_FORM = {
  outcome: 'purchased', bank: '', line1: '', line2: '', postcode: '',
  product: '', asin: '', account: '', region: '', inviteType: 'product_invitation',
  inviteDate: '', loggedAt: '',
};

function formFromRow(r) {
  return {
    outcome: r.outcome, bank: r.bank || '',
    line1: r.line1 || '', line2: r.line2 || '', postcode: r.postcode || '',
    product: r.product || '', asin: r.asin || '', account: r.account || '',
    region: r.region || '', inviteType: r.inviteType || '',
    inviteDate: toLocalInput(r.inviteDate), loggedAt: toLocalInput(r.loggedAt),
  };
}

// One editor for both amending an existing record and adding one retrospectively.
// Keyed on the record id by its caller: the form is seeded from `row` once, so
// switching target without a remount would show the previous record's values
// while saving them onto the new one.
function RecordEditor({ row, onSaved, onCancel, isMobile }) {
  const [form, setForm]     = useState(() => (row ? formFromRow(row) : { ...BLANK_FORM, loggedAt: toLocalInput(new Date().toISOString()) }));
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);
  const isNew = !row;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true);
    setError(null);
    // Every field is sent on each save, so clearing one actually clears it
    // rather than leaving the previous value in place.
    const payload = {
      outcome: form.outcome,
      bank: form.bank.trim() || null,
      address: { line1: form.line1.trim(), line2: form.line2.trim(), postcode: form.postcode.trim().toUpperCase() },
      invite: {
        summary: form.product.trim() || null,
        asin: form.asin.trim().toUpperCase() || null,
        accountEmail: form.account.trim().toLowerCase() || null,
        region: form.region.trim().toUpperCase() || null,
        type: form.inviteType || null,
        emailDate: fromLocalInput(form.inviteDate),
      },
      loggedAt: fromLocalInput(form.loggedAt),
    };
    try {
      const res = await fetch(isNew ? '/api/invitations/logs' : `/api/invitations/${row.id}/log`, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Save failed');
      onSaved();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm('Delete this record? This cannot be undone.')) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/invitations/${row.id}/log`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outcome: null }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Delete failed');
      onSaved();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  const grid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 8 };

  return (
    <div style={{ ...CARD, padding: isMobile ? 14 : '16px 18px', marginBottom: 12, borderColor: 'rgba(99,102,241,0.35)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#f1f5f9' }}>
          {isNew ? 'Add a record' : 'Edit record'}
        </span>
        {row?.manual && <span style={{ fontSize: 10, color: '#a5b4fc', background: 'rgba(99,102,241,0.15)', borderRadius: 20, padding: '2px 8px' }}>entered by hand</span>}
        {!isNew && <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#334155' }}>{row.id}</span>}
      </div>

      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10 }}>
        {['purchased', 'cancelled', 'suspended', 'skipped'].map(o => {
          const active = form.outcome === o;
          const c = OUT[o]?.color || '#64748b';
          return (
            <button key={o} onClick={() => set('outcome', o)} style={{
              fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 20, cursor: 'pointer',
              border: `1.5px solid ${active ? c : 'rgba(255,255,255,0.12)'}`,
              background: active ? (OUT[o]?.bg || 'rgba(100,116,139,0.18)') : 'transparent',
              color: active ? c : '#64748b', textTransform: 'capitalize',
            }}>{o}</button>
          );
        })}
      </div>

      <div style={{ ...grid, marginBottom: 8 }}>
        <Field label="Bank used">
          <input style={INPUT} value={form.bank} list={BANK_LIST_ID} autoComplete="off"
                 onChange={e => set('bank', e.target.value)} placeholder="Card / bank used" />
        </Field>
        <Field label="Address line 1">
          <input style={INPUT} value={form.line1} onChange={e => set('line1', e.target.value)} placeholder="House no. & street" />
        </Field>
        <Field label="Address line 2">
          <input style={INPUT} value={form.line2} onChange={e => set('line2', e.target.value)} placeholder="Town / area" />
        </Field>
        <Field label="Postcode">
          <input style={{ ...INPUT, textTransform: 'uppercase' }} value={form.postcode} onChange={e => set('postcode', e.target.value)} placeholder="B21 9AX" />
        </Field>
        <Field label="Product">
          <input style={INPUT} value={form.product} onChange={e => set('product', e.target.value)} placeholder="Product name" />
        </Field>
        <Field label="ASIN">
          <input style={{ ...INPUT, textTransform: 'uppercase' }} value={form.asin} onChange={e => set('asin', e.target.value)} placeholder="B0XXXXXXXX" />
        </Field>
        <Field label="Account email">
          <input style={INPUT} value={form.account} onChange={e => set('account', e.target.value)} placeholder="name@domain" />
        </Field>
        <Field label="Region">
          <input style={{ ...INPUT, textTransform: 'uppercase' }} value={form.region} onChange={e => set('region', e.target.value)} placeholder="UK" />
        </Field>
        <Field label="Invite type">
          <select style={INPUT} value={form.inviteType} onChange={e => set('inviteType', e.target.value)}>
            <option value="">—</option>
            <option value="product_invitation">product_invitation</option>
            <option value="vine">vine</option>
            <option value="early_reviewer">early_reviewer</option>
          </select>
        </Field>
        <Field label="Invite received" hint="drives “time from invite to order”">
          <input type="datetime-local" style={INPUT} value={form.inviteDate} onChange={e => set('inviteDate', e.target.value)} />
        </Field>
        <Field label="Order date" hint="the date this is analysed under">
          <input type="datetime-local" style={INPUT} value={form.loggedAt} onChange={e => set('loggedAt', e.target.value)} />
        </Field>
      </div>

      {error && <div style={{ fontSize: 11, color: '#f87171', marginBottom: 8 }}>⚠ {error}</div>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={save} disabled={saving} style={{
          fontSize: 12, fontWeight: 600, padding: '7px 16px', borderRadius: 8, cursor: saving ? 'wait' : 'pointer',
          border: 'none', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', opacity: saving ? 0.6 : 1,
        }}>
          {saving ? 'Saving…' : isNew ? 'Add record' : 'Save changes'}
        </button>
        <button onClick={onCancel} disabled={saving} style={{
          fontSize: 12, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
          border: '1px solid rgba(255,255,255,0.12)', background: 'transparent', color: '#64748b',
        }}>Cancel</button>
        <div style={{ flex: 1 }} />
        {!isNew && (
          <button onClick={remove} disabled={saving} style={{
            fontSize: 11, padding: '7px 12px', borderRadius: 8, cursor: 'pointer',
            border: '1px solid rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.08)', color: '#f87171',
          }}>Delete</button>
        )}
      </div>
    </div>
  );
}

function downloadCsv(rows) {
  const q = v => `"${String(v === null || v === undefined ? '' : v).replace(/"/g, '""')}"`;
  const body = rows.map(r => COLUMNS.map(([k]) => q(r[k])).join(','));
  const csv  = [COLUMNS.map(([, l]) => q(l)).join(','), ...body].join('\n');
  const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a    = document.createElement('a');
  a.href = url;
  a.download = `quality_check_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Main ────────────────────────────────────────────────────────────────────

export default function QualityCheck({ isMobile }) {
  const [entries, setEntries] = useState(null);
  const [error, setError]     = useState(null);
  const [tab, setTab]         = useState('compare'); // compare | raw
  const [banks, setBanks]     = useState([]);
  // null = closed, { row } = amending, { row: null } = adding
  const [editing, setEditing] = useState(null);
  const [includeRef, setIncludeRef] = useState(false);

  const load = useCallback(() => {
    fetch('/api/invitations/logs')
      .then(r => r.json())
      .then(d => setEntries(Array.isArray(d) ? d : []))
      .catch(e => setError(e.message));
    fetch('/api/invitations/banks')
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setBanks(d); })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(
    () => (entries || [])
      .filter(e => e.outcome === 'purchased' || e.outcome === 'cancelled')
      .map(enrich),
    [entries]
  );

  if (error)   return <div style={{ padding: 28, color: '#f87171', fontSize: 13 }}>⚠ {error}</div>;
  if (!entries) return <div style={{ padding: 28, color: '#475569', fontSize: 13 }}>Loading logged orders…</div>;

  const datalist = (
    <datalist id={BANK_LIST_ID}>
      {banks.map(b => <option key={b.value} value={b.value} />)}
    </datalist>
  );

  const closeEditor = () => setEditing(null);
  const savedEditor = () => { setEditing(null); load(); };

  if (!rows.length) {
    return (
      <div>
        {datalist}
        {editing ? (
          <RecordEditor key={editing.row?.id || 'new'} row={editing.row} onSaved={savedEditor} onCancel={closeEditor} isMobile={isMobile} />
        ) : (
          <div style={{ textAlign: 'center', padding: '44px 0', color: '#334155', fontSize: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 32 }}>🔍</span>
            No purchased or cancelled orders logged yet — record outcomes on the Invitations tab,
            <br />or add a past order here.
            <button onClick={() => setEditing({ row: null })} style={{
              fontSize: 12, fontWeight: 600, padding: '8px 18px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', cursor: 'pointer',
            }}>+ Add a record</button>
          </div>
        )}
      </div>
    );
  }

  // Reference patterns are not our orders. They stay out of the rates unless
  // asked for, so the comparison always describes our own performance first.
  const refRows = rows.filter(r => r.reference);
  const own     = rows.filter(r => !r.reference);
  const compareRows = includeRef ? rows : own;

  const purchased = own.filter(r => r.outcome === 'purchased').length;
  const cancelled = own.length - purchased;
  const withAddr  = own.filter(r => r.postcode || r.line1).length;
  const withBank  = own.filter(r => r.bank).length;
  const withInv   = own.filter(r => r.asin || r.product).length;

  return (
    <div>
      {datalist}
      {editing && (
        <RecordEditor key={editing.row?.id || 'new'} row={editing.row} onSaved={savedEditor} onCancel={closeEditor} isMobile={isMobile} />
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <Tile label="Logged orders" value={own.length} sub="purchased + cancelled" accent="#818cf8" />
        <Tile label="Purchased" value={purchased} accent={OUT.purchased.color} />
        <Tile label="Cancelled" value={cancelled}
              sub={own.length ? `${Math.round((cancelled / own.length) * 100)}% of logged` : null}
              accent={OUT.cancelled.color} />
        <Tile label="With address" value={`${withAddr}/${own.length}`} accent={withAddr === own.length ? '#34d399' : '#fbbf24'} />
        <Tile label="With bank" value={`${withBank}/${own.length}`} accent={withBank === own.length ? '#34d399' : '#fbbf24'} />
        <Tile label="With product" value={`${withInv}/${own.length}`} accent={withInv === own.length ? '#34d399' : '#fbbf24'} />
      </div>

      {(withAddr < own.length || withBank < own.length || withInv < own.length) && (
        <div style={{
          background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.22)',
          borderRadius: 10, padding: '10px 14px', fontSize: 12, marginBottom: 14, color: '#fcd34d', lineHeight: 1.6,
        }}>
          Not every order carries every datapoint — the address, bank and product fields were added after the
          earliest outcomes were logged, so those rows are blank rather than wrong. Each breakdown below counts
          only the orders that actually hold the field, and says how many it skipped.
        </div>
      )}

      {refRows.length > 0 && (
        <div style={{
          background: 'rgba(129,140,248,0.08)', border: '1px solid rgba(129,140,248,0.25)',
          borderRadius: 10, padding: '10px 14px', fontSize: 12, marginBottom: 14,
          color: '#a5b4fc', lineHeight: 1.6, display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
        }}>
          <span style={{ flex: 1, minWidth: 240 }}>
            <strong>{refRows.length} reference pattern{refRows.length === 1 ? '' : 's'}</strong> held for guidance —
            {' '}address{refRows.length === 1 ? '' : 'es'} known to have gone through, but not orders you placed.
            Kept out of the rates above so they describe your own orders only. They carry no order date, so the
            day, hour and time-to-order breakdowns treat them as blank.
          </span>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={includeRef} onChange={e => setIncludeRef(e.target.checked)} />
            <span style={{ fontSize: 11 }}>include in comparison</span>
          </label>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.03)', borderRadius: 10, padding: 4 }}>
          {[['compare', 'Compare datapoints'], ['raw', `Raw data (${rows.length})`]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              fontSize: 11, fontWeight: 600, padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: tab === key ? '#334155' : 'transparent',
              color: tab === key ? '#f1f5f9' : '#64748b', whiteSpace: 'nowrap',
            }}>{label}</button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setEditing({ row: null })} style={{
          fontSize: 11, fontWeight: 600, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
          border: '1px solid rgba(99,102,241,0.35)', background: 'rgba(99,102,241,0.12)', color: '#a5b4fc',
        }}>
          + Add record
        </button>
        <button onClick={() => downloadCsv(rows)} style={{
          fontSize: 11, fontWeight: 600, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
          border: '1px solid rgba(52,211,153,0.3)', background: 'rgba(52,211,153,0.1)', color: '#34d399',
        }}>
          ↓ CSV
        </button>
      </div>

      {tab === 'compare' ? (
        <>
          <div style={{ fontSize: 11, color: '#475569', marginBottom: 12, lineHeight: 1.6 }}>
            Each datapoint split by outcome, commonest value first. <span style={{ color: '#94a3b8' }}>Read the counts, not
            the percentages</span> — anything under {THIN} orders is marked <em>thin</em> and a 100% rate over one
            order says nothing. A dimension separates the outcomes only where the same value appears on both sides.
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(370px, 1fr))',
            gap: isMobile ? 10 : 12, alignItems: 'start',
          }}>
            {DIMENSIONS.map(d => (
              <DimensionCard key={d.key} label={d.label} dimKey={d.key} rows={compareRows} isMobile={isMobile} />
            ))}
          </div>
        </>
      ) : (
        <RawTable rows={rows} isMobile={isMobile} onEdit={row => setEditing({ row })} />
      )}
    </div>
  );
}
